// Reiter „Flow“ (28.10.2026, Till): im dunklen Flow-Layout links eine Spalte mit Umschalter
// Inbox | Meine Aufgaben, rechts der Kalender (ausblendbar). Inbox und Aufgaben sprechen über die mail-api
// mit dem Flow-Service-Zugang (handelt als die angemeldete Person). Optik: Tokens und Klassen 1:1 aus
// flow.minimusiker.de (styles.css, Abschnitt .flow-dark).
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import {
  api,
  type FlowAufgabe,
  type FlowAufgaben,
  type FlowInboxItem,
  type FlowPrio,
  type FlowStatus,
} from '../lib/api.ts';
import { Kalender } from '../components/Kalender.tsx';
import { toast } from '../components/Toast.tsx';

const PRIO_CYCLE: FlowPrio[] = ['low', 'medium', 'high', 'urgent'];
const PRIO_LABEL: Record<FlowPrio, string> = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  urgent: 'Dringend',
};
const STATUS_CYCLE: FlowStatus[] = ['todo', 'in_progress', 'done'];
const STATUS_LABEL: Record<FlowStatus, string> = {
  todo: 'offen',
  in_progress: 'in Arbeit',
  done: 'erledigt',
};

// Schnellsyntax wie in Flow: Text endet mit „!“ → hoch, „!!“ → dringend
function parseQuick(raw: string): { text: string; priority: FlowPrio } {
  const trimmed = raw.trim();
  const m = /^(.*?)\s*(!{1,3})$/.exec(trimmed);
  if (!m) return { text: trimmed, priority: 'medium' };
  const body = m[1]!.trim();
  if (!body) return { text: trimmed, priority: 'medium' };
  return { text: body, priority: m[2]!.length >= 2 ? 'urgent' : 'high' };
}

function PrioBars({ priority }: { priority: FlowPrio }) {
  return (
    <span className={`priority-icon priority-${priority}`} aria-label={PRIO_LABEL[priority]}>
      <span className="priority-bars">
        <span />
        <span />
        <span />
      </span>
    </span>
  );
}

// Alle 60 s und beim Sichtbarwerden des Fensters neu laden (kein SSE aus Flow in der Mail-App)
function useAuffrischen(load: () => void, key: unknown = null) {
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    const vis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', vis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', vis);
    };
  }, [load, key]);
}

const stored = <T,>(k: string, d: T): T => {
  try {
    const v = localStorage.getItem(k);
    return v == null ? d : (JSON.parse(v) as T);
  } catch {
    return d;
  }
};
const remember = (k: string, v: unknown) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* privates Fenster o. ä. */
  }
};

type Tab = 'inbox' | 'aufgaben';

export function FlowPage() {
  const [status, setStatus] = useState<{ available: boolean; url: string } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [tab, setTab] = useState<Tab>(() => stored<Tab>('mm.flow.tab', 'aufgaben'));
  const [kal, setKal] = useState(() => stored('mm.flow.kal', true));
  useEffect(() => {
    void api.flowStatus().then(setStatus);
  }, []);
  useEffect(() => {
    remember('mm.flow.tab', tab);
    remember('mm.flow.kal', kal);
  }, [tab, kal]);
  const url = status?.url ?? '';
  const bump = useCallback(() => setRefresh((x) => x + 1), []);

  // Umschalter Inbox | Aufgaben (gleiche Optik wie Mail | Flow in der Seitenleiste)
  const tabs = (
    <div className={`side-tabs ftabs${tab === 'aufgaben' ? ' flow' : ''}`} role="tablist">
      <span className="thumb" aria-hidden="true" />
      <button
        role="tab"
        aria-selected={tab === 'inbox'}
        className={tab === 'inbox' ? 'on' : ''}
        onClick={() => setTab('inbox')}
      >
        Inbox
      </button>
      <button
        role="tab"
        aria-selected={tab === 'aufgaben'}
        className={tab === 'aufgaben' ? 'on' : ''}
        onClick={() => setTab('aufgaben')}
      >
        Meine Aufgaben
      </button>
    </div>
  );
  // Rechts nur Symbole, damit die Kopfzeile in der schmalen Spalte einzeilig bleibt
  const rechts = (
    <span className="fhead-r">
      {url && (
        <a
          className="icon-btn"
          href={`${url}/${tab === 'inbox' ? 'inbox' : 'aufgaben'}`}
          target="_blank"
          rel="noreferrer"
          title="In Flow öffnen"
          aria-label="In Flow öffnen"
        >
          ↗
        </a>
      )}
      <button
        className={`fkal-toggle${kal ? ' on' : ''}`}
        onClick={() => setKal((v) => !v)}
        title={kal ? 'Kalender ausblenden' : 'Kalender einblenden'}
        aria-pressed={kal}
        aria-label={kal ? 'Kalender ausblenden' : 'Kalender einblenden'}
      >
        ▦
      </button>
    </span>
  );
  return (
    <section
      className={`page-body flow-page flow-dark${kal ? '' : ' ohne-kal'}`}
      aria-label="Flow: Inbox, Aufgaben, Kalender"
    >
      {tab === 'inbox' ? (
        <InboxSpalte url={url} onChanged={bump} tabs={tabs} rechts={rechts} />
      ) : (
        <AufgabenSpalte url={url} refresh={refresh} onChanged={bump} tabs={tabs} rechts={rechts} />
      )}
      {kal && (
        <div className="fcol fcol-kal">
          <Kalender refresh={refresh} />
        </div>
      )}
    </section>
  );
}

// ---------- Spalte 1: persönliche Inbox ----------
function InboxSpalte({
  onChanged,
  tabs,
  rechts,
}: {
  url: string;
  onChanged: () => void;
  tabs: ReactNode;
  rechts: ReactNode;
}) {
  const [items, setItems] = useState<FlowInboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const captureRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    void api
      .flowInbox()
      .then((r) => {
        setItems(r.items);
        setError(
          r.error ?? (r.available ? null : 'Flow ist nicht angebunden (Service-Token fehlt).'),
        );
      })
      .catch((e: Error) => setError(e.message));
  }, []);
  useAuffrischen(load);

  const preview = draft.trim() ? parseQuick(draft).priority : null;
  const anlegen = async (e: FormEvent) => {
    e.preventDefault();
    const raw = draft;
    if (!raw.trim()) return;
    setDraft('');
    try {
      const { text, priority } = parseQuick(raw);
      const r = await api.flowInboxAnlegen({ text, priority });
      setItems((l) => [r.item, ...(l ?? [])]);
    } catch (err) {
      toast((err as Error).message);
      setDraft(raw);
    }
    captureRef.current?.focus();
  };
  const entfernen = async (id: number) => {
    const vorher = items;
    setItems((l) => (l ?? []).filter((i) => i.id !== id));
    try {
      await api.flowInboxLoeschen(id);
    } catch (err) {
      toast((err as Error).message);
      setItems(vorher);
    }
  };
  const aendern = async (id: number, patch: { text?: string; priority?: FlowPrio }) => {
    setItems((l) => (l ?? []).map((i) => (i.id === id ? { ...i, ...patch } : i)));
    try {
      await api.flowInboxAendern(id, patch);
    } catch (err) {
      toast((err as Error).message);
      load();
    }
  };
  // Umwandeln: erst anlegen, dann die Notiz entfernen (wie in Flow)
  const zuAufgabe = async (it: FlowInboxItem) => {
    try {
      const r = await api.flowAufgabeAnlegen({ title: it.text, priority: it.priority });
      await api.flowInboxLoeschen(it.id);
      setItems((l) => (l ?? []).filter((i) => i.id !== it.id));
      toast(`Aufgabe ${r.task.id} angelegt.`);
      onChanged();
    } catch (err) {
      toast((err as Error).message);
    }
  };
  const zuTermin = async (it: FlowInboxItem) => {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
    const end = new Date(start.getTime() + 3600e3);
    try {
      await api.flowTerminAnlegen({
        title: it.text,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      await api.flowInboxLoeschen(it.id);
      setItems((l) => (l ?? []).filter((i) => i.id !== it.id));
      toast(
        `Termin angelegt: heute ${start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr – im Kalender verschieben.`,
      );
      onChanged();
    } catch (err) {
      toast((err as Error).message);
    }
  };

  const list = items ?? [];
  const urgent = list.filter((i) => i.priority === 'urgent');
  const rest = list.filter((i) => i.priority !== 'urgent');
  return (
    <div className="fcol fcol-list">
      <div className="fcol-head">
        {tabs}
        <span className="sub">
          {list.length === 0
            ? 'nur für dich sichtbar'
            : `${list.length} ${list.length === 1 ? 'Notiz' : 'Notizen'} · nur für dich`}
        </span>
        {rechts}
      </div>
      <div className="fcol-body">
        <form className="inbox-capture" onSubmit={(e) => void anlegen(e)}>
          <input
            ref={captureRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Was liegt an? Einfach reinschreiben …"
            maxLength={500}
          />
          {preview === 'urgent' && <span className="qc-badge urgent">Dringend</span>}
          {preview === 'high' && <span className="qc-badge high">Hoch</span>}
          <span className="capture-enter">↵</span>
        </form>
        <div className="inbox-hint">
          Enter legt an. Endet der Text mit <kbd>!</kbd>, ist er hoch priorisiert, mit <kbd>!!</kbd>{' '}
          dringend.
        </div>
        {error && <div className="f-error">{error}</div>}
        {items && list.length === 0 && !error && (
          <div className="inbox-empty">
            Noch nichts drin.
            <div className="inbox-empty-hint">
              Alles, was dir zwischendurch einfällt, kommt hier rein – sortieren kannst du später.
            </div>
          </div>
        )}
        {!items && !error && <div className="f-muted">Lädt …</div>}
        {urgent.length > 0 && <div className="inbox-group-label">Dringend</div>}
        {urgent.map((it) => (
          <InboxZeile
            key={it.id}
            it={it}
            onText={(t) => void aendern(it.id, { text: t })}
            onPrio={(p) => void aendern(it.id, { priority: p })}
            onAufgabe={() => void zuAufgabe(it)}
            onTermin={() => void zuTermin(it)}
            onLoeschen={() => void entfernen(it.id)}
          />
        ))}
        {urgent.length > 0 && rest.length > 0 && <div className="inbox-group-label">Weitere</div>}
        {rest.map((it) => (
          <InboxZeile
            key={it.id}
            it={it}
            onText={(t) => void aendern(it.id, { text: t })}
            onPrio={(p) => void aendern(it.id, { priority: p })}
            onAufgabe={() => void zuAufgabe(it)}
            onTermin={() => void zuTermin(it)}
            onLoeschen={() => void entfernen(it.id)}
          />
        ))}
      </div>
    </div>
  );
}

function InboxZeile({
  it,
  onText,
  onPrio,
  onAufgabe,
  onTermin,
  onLoeschen,
}: {
  it: FlowInboxItem;
  onText: (t: string) => void;
  onPrio: (p: FlowPrio) => void;
  onAufgabe: () => void;
  onTermin: () => void;
  onLoeschen: () => void;
}) {
  const [text, setText] = useState(it.text);
  useEffect(() => setText(it.text), [it.text]);
  const naechste = PRIO_CYCLE[(PRIO_CYCLE.indexOf(it.priority) + 1) % PRIO_CYCLE.length]!;
  return (
    <div className={`inbox-item prio-${it.priority}`}>
      <button
        className="inbox-prio"
        type="button"
        onClick={() => onPrio(naechste)}
        title={`Priorität: ${PRIO_LABEL[it.priority]} – klicken zum Wechseln`}
      >
        <PrioBars priority={it.priority} />
      </button>
      <input
        className="inbox-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text.trim() && text.trim() !== it.text) onText(text.trim());
          else if (!text.trim()) setText(it.text);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setText(it.text);
            e.currentTarget.blur();
          }
        }}
      />
      {it.priority === 'urgent' && <span className="inbox-urgent-flag">Dringend</span>}
      <div className="inbox-actions">
        <button
          className="inbox-convert"
          type="button"
          onClick={onAufgabe}
          title="In eine Aufgabe umwandeln (Standardprojekt, du bist zuständig)"
        >
          ✓ Aufgabe
        </button>
        <button
          className="inbox-convert"
          type="button"
          onClick={onTermin}
          title="In einen Termin umwandeln (nächste volle Stunde, 1 h)"
        >
          ▦ Termin
        </button>
        <button
          className="icon-btn danger"
          type="button"
          onClick={onLoeschen}
          title="Notiz löschen"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ---------- Spalte 2: Meine Aufgaben ----------
type Gruppe = { key: string; label: string; aufgaben: FlowAufgabe[] };
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
function gruppieren(list: FlowAufgabe[]): Gruppe[] {
  const today = startOfToday();
  const in7 = new Date(today);
  in7.setDate(in7.getDate() + 7);
  const b: Record<string, FlowAufgabe[]> = {
    overdue: [],
    today: [],
    week: [],
    later: [],
    none: [],
  };
  for (const t of list) {
    if (!t.dueDate) {
      b['none']!.push(t);
      continue;
    }
    const d = new Date(t.dueDate);
    const tag = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (tag < today) b['overdue']!.push(t);
    else if (tag.getTime() === today.getTime()) b['today']!.push(t);
    else if (tag < in7) b['week']!.push(t);
    else b['later']!.push(t);
  }
  const byDue = (a: FlowAufgabe, c: FlowAufgabe) =>
    (a.dueDate ?? '').localeCompare(c.dueDate ?? '');
  const order: [string, string][] = [
    ['overdue', 'Überfällig'],
    ['today', 'Heute fällig'],
    ['week', 'Diese Woche'],
    ['later', 'Später'],
    ['none', 'Ohne Fälligkeit'],
  ];
  return order
    .map(([key, label]) => ({ key, label, aufgaben: b[key]!.slice().sort(byDue) }))
    .filter((g) => g.aufgaben.length > 0);
}
function faelligText(date: Date): { label: string; cls: string } {
  const today = startOfToday();
  const tag = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((tag.getTime() - today.getTime()) / 86_400_000);
  const zeit = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (diff < 0) return { label: `${Math.abs(diff)}T über`, cls: 'overdue' };
  if (diff === 0) return { label: `Heute ${zeit}`, cls: 'today' };
  if (diff === 1) return { label: `Morgen ${zeit}`, cls: 'soon' };
  if (diff < 7) return { label: date.toLocaleDateString('de-DE', { weekday: 'short' }), cls: '' };
  return { label: date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }), cls: '' };
}

function AufgabenSpalte({
  url,
  refresh,
  onChanged,
  tabs,
  rechts,
}: {
  url: string;
  refresh: number;
  onChanged: () => void;
  tabs: ReactNode;
  rechts: ReactNode;
}) {
  const [data, setData] = useState<FlowAufgaben | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const load = useCallback(() => {
    void api
      .flowAufgaben()
      .then((d) => {
        setData(d);
        setLadeFehler(null);
      })
      .catch((e: Error) => setLadeFehler(e.message));
  }, []);
  useAuffrischen(load, refresh);

  const offen = useMemo(() => (data?.aufgaben ?? []).filter((t) => t.status !== 'done'), [data]);
  const gruppen = useMemo(() => gruppieren(offen), [offen]);
  const preview = draft.trim() ? parseQuick(draft).priority : null;

  const anlegen = async (e: FormEvent) => {
    e.preventDefault();
    const raw = draft;
    if (!raw.trim()) return;
    setDraft('');
    try {
      const { text, priority } = parseQuick(raw);
      const r = await api.flowAufgabeAnlegen({ title: text, priority });
      toast(`Aufgabe ${r.task.id} angelegt.`);
      load();
      onChanged();
    } catch (err) {
      toast((err as Error).message);
      setDraft(raw);
    }
  };
  const status = async (t: FlowAufgabe, neu: FlowStatus) => {
    const vorher = t.status;
    setData((d) =>
      d
        ? { ...d, aufgaben: d.aufgaben.map((x) => (x.id === t.id ? { ...x, status: neu } : x)) }
        : d,
    );
    try {
      await api.flowAufgabeAendern(t.id, { status: neu });
      if (neu === 'done')
        toast(`${t.id} erledigt.`, {
          undo: async () => {
            await api.flowAufgabeAendern(t.id, { status: vorher });
            load();
            onChanged();
          },
        });
      onChanged();
    } catch (err) {
      toast((err as Error).message);
      load();
    }
  };
  const fehler = ladeFehler ?? data?.error ?? null;
  return (
    <div className="fcol fcol-list">
      <div className="fcol-head">
        {tabs}
        <span className="cnt">{offen.length} offen</span>
        {rechts}
      </div>
      <div className="fcol-body">
        <form className="inbox-capture" onSubmit={(e) => void anlegen(e)}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Neue Aufgabe für mich …"
            maxLength={200}
          />
          {preview === 'urgent' && <span className="qc-badge urgent">Dringend</span>}
          {preview === 'high' && <span className="qc-badge high">Hoch</span>}
          <span className="capture-enter">↵</span>
        </form>
        {fehler && <div className="f-error">{fehler}</div>}
        {data && !data.available && !fehler && (
          <div className="f-error">Flow ist nicht angebunden (Service-Token fehlt).</div>
        )}
        {!data && !fehler && <div className="f-muted">Lädt …</div>}
        {data?.available && !fehler && offen.length === 0 && (
          <div className="tl-empty">Keine offenen Aufgaben – schön.</div>
        )}
        {gruppen.map((g) => (
          <div key={g.key}>
            <div className={`tl-section-label${g.key === 'overdue' ? ' overdue' : ''}`}>
              {g.label}
              <span className="n">{g.aufgaben.length}</span>
            </div>
            <div className="tl-rows">
              {g.aufgaben.map((t) => {
                const due = t.dueDate ? faelligText(new Date(t.dueDate)) : null;
                const naechster =
                  STATUS_CYCLE[(STATUS_CYCLE.indexOf(t.status) + 1) % STATUS_CYCLE.length]!;
                return (
                  <div key={t.id} className="tl-row">
                    <button
                      className={`tl-status-dot ${t.status}`}
                      type="button"
                      onClick={() => void status(t, naechster)}
                      title={`${STATUS_LABEL[t.status]} – klicken: ${STATUS_LABEL[naechster]}`}
                      aria-label={`Status ${STATUS_LABEL[t.status]}, klicken für ${STATUS_LABEL[naechster]}`}
                    />
                    <PrioBars priority={t.priority} />
                    <span className="tl-row-id">{t.id}</span>
                    <a
                      className="tl-row-title"
                      href={url ? `${url}/task/${t.id}` : undefined}
                      target="_blank"
                      rel="noreferrer"
                      title={`${t.title}${t.projekt ? ' · ' + t.projekt : ''} – in Flow öffnen`}
                    >
                      {t.title}
                    </a>
                    <div className="tl-row-meta">
                      {t.source === 'mail' && t.externalUrl && (
                        <a className="tl-mail" href={t.externalUrl} title="Mail öffnen">
                          ✉︎
                        </a>
                      )}
                      {t.projektFarbe && (
                        <span
                          className="tl-project-dot"
                          style={{ background: t.projektFarbe }}
                          title={t.projekt ?? ''}
                        />
                      )}
                      <span className={`tl-due ${due?.cls ?? ''}`}>{due?.label ?? '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div className="f-muted" style={{ marginTop: 'auto', paddingTop: 14 }}>
          Schulbezogene Erinnerungen liegen im CRM (Bobs Tagesüberblick). Hier stehen Teamaufgaben
          aus Flow.
        </div>
      </div>
    </div>
  );
}
